import { Link } from "react-router-dom";
import { Users, Eye, Activity, Briefcase, CheckCircle2, IndianRupee, Clock, Megaphone, ArrowUpRight, Instagram } from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { StatCard, LoadingGrid, ErrorState, Avatar, StatusBadge, EmptyState } from "@/components/common";
import { useBranding } from "@/context/BrandingContext";
import { inr, compact, fdate, ago } from "@/lib/format";

export default function CreatorDashboard() {
  const { data, loading, error, reload } = useApi("/me/dashboard");
  const { branding } = useBranding();
  if (loading) return <LoadingGrid count={8} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const p = data.profile;
  const e = data.earnings;
  return (
    <div className="space-y-10">
      <section className="uc-card p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6 relative overflow-hidden fade-up" data-testid="creator-hero">
        <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
        <Avatar src={p.profile_picture} name={p.full_name} className="h-20 w-20 sm:h-24 sm:w-24" />
        <div className="flex-1 min-w-0 relative">
          <p className="eyebrow text-primary" data-testid="managed-by-label">Managed by {branding.website_name}</p>
          <h1 className="text-3xl sm:text-4xl font-semibold mt-1 truncate" data-testid="creator-name">{p.full_name}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
            <a href={p.instagram_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-primary" data-testid="creator-instagram-handle"><Instagram className="h-4 w-4" />@{p.instagram_username}</a>
            <span className="rounded-full border border-border px-2.5 py-0.5 text-xs" data-testid="creator-category">{p.category_name}</span>
            <StatusBadge status={p.status} testid="creator-account-status" />
          </div>
        </div>
      </section>

      <section>
        <p className="eyebrow mb-4">Instagram performance</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 stagger">
          <StatCard label="Followers" value={compact(p.followers)} icon={Users} testid="stat-followers" />
          <StatCard label="Avg reel views" value={compact(p.avg_reel_views)} icon={Eye} testid="stat-avg-views" />
          <StatCard label="Engagement" value={`${Number(p.engagement || 0).toFixed(1)}%`} icon={Activity} testid="stat-engagement" />
          <StatCard label="Total deals" value={data.total_deals} icon={Briefcase} testid="stat-total-deals" />
        </div>
      </section>

      <section>
        <p className="eyebrow mb-4">Collaborations & earnings</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 stagger">
          <StatCard label="Active deals" value={data.active_deals} icon={Briefcase} testid="stat-active-deals" />
          <StatCard label="Completed" value={data.completed_deals} icon={CheckCircle2} testid="stat-completed-deals" />
          <StatCard label="Total earnings" value={inr(e.creator_payout)} icon={IndianRupee} testid="stat-total-earnings" accent />
          <StatCard label="Pending earnings" value={inr(e.pending)} icon={Clock} testid="stat-pending-earnings" />
        </div>
      </section>

      <div className="grid lg:grid-cols-5 gap-6">
        <section className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <p className="eyebrow">Recent collaborations</p>
            <Link to="/deals" className="text-xs text-primary hover:underline" data-testid="view-all-deals-link">View all</Link>
          </div>
          {data.recent_deals.length === 0 ? (
            <EmptyState icon={Briefcase} title="No collaborations yet" desc="New opportunities will appear here." testid="dashboard-deals-empty" />
          ) : (
            <div className="space-y-2.5">
              {data.recent_deals.map((d) => (
                <Link key={d.id} to={`/deals/${d.id}`} className="uc-card uc-card-hover p-4 flex items-center justify-between gap-4" data-testid={`dashboard-deal-${d.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{d.brand_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{d.campaign_name} · Due {fdate(d.deadline)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="num text-sm text-primary">{inr(d.creator_payout)}</p>
                    <StatusBadge status={d.campaign_status} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
        <section className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <p className="eyebrow">Channel</p>
            <Link to="/channel" className="text-xs text-primary hover:underline" data-testid="view-channel-link">Open channel</Link>
          </div>
          {data.recent_posts.length === 0 ? (
            <EmptyState icon={Megaphone} title="No announcements yet." testid="dashboard-channel-empty" />
          ) : (
            <div className="space-y-2.5">
              {data.recent_posts.map((post) => (
                <Link key={post.id} to={`/channel/${post.id}`} className="uc-card uc-card-hover p-4 block" data-testid={`dashboard-post-${post.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-medium line-clamp-1">{post.title || post.body}</p>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{ago(post.created_at)}</p>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
